import { audioCtx } from './audio-engine';
import { createBeds, pulse, sub } from './music-layers';
import { TrackId, currentTrack, playTrack, trackDebug, trackPending } from './music-tracks';
/* ============================================================
   音樂 —— 音軌切換（主）＋ 程序生成（備援）
   ============================================================

   ## 現況（2026-08-06 下午）

   使用者提供了兩首曲子，於是**音檔是主要來源**：`main-theme` 給主畫面、
   `level-1` 給對局（播放層在 `music-tracks.ts`，入口是下面的 `setScene`）。

   下面那一整套程序生成從主角退成**備援**。它沒有被拿掉，因為它解的問題還在：

   ## 程序層當初為什麼存在（仍然成立）

   1. **不跟音效打架。** 音效住 400 Hz–1.8 kHz，這套只用它的上下兩端
      （頻段表見 `music-layers.ts`）。成品音樂是全頻譜混音，做不到這件事。
   2. **強度是連續的，不是切曲。** 20 分鐘從「空曠」推到「淹沒」是一條斜坡，
      素材包只能在幾首曲子之間跳，跳的那一刻玩家會聽出接縫。
   3. **零檔案大小。** 這是 web 遊戲，8–12 軌 OGG 是 20–40 MB，直接打在首次載入上。

   做不到的是**旋律記憶點** —— 那正是使用者這兩首曲子帶進來的東西。
   之後若要做「音檔打底＋程序層跟著強度疊上去」的混合，接口就在這裡。

   ⚠️ 音檔是全頻譜混音，**沒有**程序層那個「讓開 400–1800 Hz」的性質。
   如果實際遊玩覺得音樂跟音效互相蓋住，`music-tracks.ts` 的 deck 上加一顆
   等化器（把 400–1800 Hz 挖 3–5 dB）是最省事的解法。

   ## 排程

   節拍用 **lookahead scheduler**：每 `TICK` 毫秒醒來一次，把未來 `LOOK` 秒內
   該響的音用 `ctx.currentTime + 偏移` 排進 WebAudio 的時鐘。
   **不能用 `setTimeout` 直接播音符** —— 那個抖動在音效上聽不出來，在節拍上
   聽得非常清楚（會像鼓手喝醉）。

   ## 決定性

   音樂完全不進模擬快照，用系統時鐘與 `Math.random()` 都是安全的
   （對局亂數是 `core/rng`，兩者不可混用 —— lessons 2026-08-01）。 */

/* ── 場景 → 音軌（2026-08-06 使用者提供了兩首曲子）──────────────
   使用者的原話：「一個當主畫面一個當關卡」。畫面切換在這個專案裡是分散的
   （各處自己 `classList.add('show')`），所以**不逐點接線**，改成每幀依狀態
   判斷一次 —— `playTrack` 對同一個音軌是 no-op，重複呼叫沒有成本。

   程序音樂（下面那一整套）從主角退成**備援**：只在音檔還沒放進來、
   或載入失敗時才會用到。它的價值仍在（強度連續、不佔音效頻段），
   之後要做「音檔＋動態層」的混合也是從這裡接。 */
export function setScene(s: 'menu' | 'run' | null) {
  var want: TrackId | null = s === 'run' ? 'level' : (s === 'menu' ? 'main' : null);
  lastScene = s;
  if (want === currentTrack()) return;
  playTrack(want);
}
var lastScene: string | null = null;

/* Debug hook。音樂的失效模式全部是**安靜的**（沒有 context、被自動播放擋下、
   元素在播但 context 是 suspended）—— 沒有這個就只能猜。 */
if (typeof window !== 'undefined') (window as any).__music = function () {
  var c = audioCtx();
  return {
    scene: lastScene, track: currentTrack(), pending: trackPending(),
    ctx: c ? c.state : 'none',
    decks: trackDebug()
  };
};

var LOOK = 0.15;      // 排程未來 150 ms
var TICK = 30;        // 每 30 ms 醒一次

var beds: any = null;
var timer: any = null;
var nextT = 0;        // 下一拍的絕對時間
var step = 0;         // 第幾個十六分音符
var cur = 0;          // 目前強度（平滑後）
var want = 0;         // 目標強度

/** BPM 隨強度上升：84（機庫、開場）→ 132（被淹沒）。
    音樂的推進感有一半來自這條線，不是來自加了幾層。 */
function bpm() { return 84 + cur * 48; }

/** 目標強度 0–1。每幀給沒關係，內部會平滑（直接跳會聽得出來）。 */
export function setIntensity(v: number) {
  want = Math.max(0, Math.min(1, v || 0));
}

export function musicIntensity() { return cur; }
export function musicRunning() { return !!timer; }

/** 啟動**程序音樂**（備援層與測試台用；正常遊玩走 `setScene` 的音檔）。 */
export function startMusic() {
  var ctx = audioCtx();
  if (!ctx || timer) return false;
  beds = createBeds();
  if (!beds) return false;
  cur = want;
  beds.set(cur);
  nextT = ctx.currentTime + 0.1;
  step = 0;
  timer = setInterval(tick, TICK);
  return true;
}

export function stopMusic() {
  if (timer) { clearInterval(timer); timer = null; }
  if (beds) { beds.stop(); beds = null; }
}

function tick() {
  var ctx = audioCtx();
  if (!ctx) return;
  /* 強度平滑：每次 tick 往目標走一小步。0.02/tick @ 30 ms ≈ 1.5 秒走完全程 ——
     快到跟得上戰況，慢到不會在波次之間抽動。 */
  cur += Math.max(-0.02, Math.min(0.02, want - cur));
  if (beds) beds.set(cur);

  var spb = 60 / bpm() / 4;          // 一個十六分音符的秒數
  while (nextT < ctx.currentTime + LOOK) {
    emit(step, nextT);
    nextT += spb;
    step = (step + 1) % 16;
  }
}

/** 一個十六分音符位置該響什麼。

    節奏型刻意極簡：**四四拍的正拍脈衝**（0/4/8/12），強度上來之後
    在反拍補音、低音動機進場。複雜的節奏型在這個題材會變成「有人在打鼓」，
    而這裡要的是「機器在運轉」。 */
function emit(s: number, t: number) {
  var k = cur;
  // 正拍：永遠在。第 0 拍重一點（那是小節線，沒有它會聽不出循環）
  if (s % 4 === 0) pulse(t, (s === 0 ? 0.085 : 0.062) * (0.6 + k * 0.4), s === 0);
  // 反拍：強度 0.3 起，越高越明顯
  if (k > 0.3 && s % 4 === 2) pulse(t, 0.03 * (k - 0.3) / 0.7 + 0.012, false);
  // 十六分的碎拍：只在高強度，而且只在小節後半（前半留空＝有呼吸）
  if (k > 0.72 && s >= 8 && s % 2 === 1) pulse(t, 0.016, false);
  // 低音動機：強度 0.45 起。第 0 拍走根音、第 10 個十六分走五度
  if (k > 0.45) {
    var v = 0.05 * Math.min(1, (k - 0.45) / 0.4);
    if (s === 0) sub(t, v, false);
    if (s === 10) sub(t, v * 0.8, true);
  }
}
